import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === "string");
}

export function hasBrandValue(value: unknown): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return typeof value === "string" ? !!value.trim() : true;
}

const labelFor = (key: string) => key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");

/** Render JSON leaves, never objects, as React children. Updates replace only
 * the edited leaf; unknown siblings and null metadata remain untouched. */
export function BrandJsonField({ value, onChange, label, depth = 0 }: {
  value: unknown;
  onChange: (value: unknown) => void;
  label: string;
  depth?: number;
}) {
  if (depth > 12) return <p>Additional nested data retained unchanged.</p>;
  if (typeof value === "string") {
    return <label className="block space-y-1 text-xs">
      <span className="capitalize">{label}</span>
      <Textarea aria-label={label} value={value} onChange={e => onChange(e.target.value)} />
    </label>;
  }
  if (Array.isArray(value)) {
    return <div className="space-y-2" role="group" aria-label={label}>
      {value.map((entry, index) => <div key={index} className="rounded-lg border p-3 space-y-2">
        <BrandJsonField value={entry} label={`${label} ${index + 1}`} depth={depth + 1}
          onChange={next => onChange(value.map((old, i) => i === index ? next : old))} />
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" disabled={index === 0}
            aria-label={`Move ${label} ${index + 1} up`} onClick={() => {
              const next = [...value];
              [next[index - 1], next[index]] = [next[index], next[index - 1]];
              onChange(next);
            }}>Move up</Button>
          <Button type="button" size="sm" variant="ghost"
            aria-label={`Remove ${label} ${index + 1}`}
            onClick={() => onChange(value.filter((_, i) => i !== index))}>Remove</Button>
        </div>
      </div>)}
      {isStringList(value) && <Input aria-label={`Add ${label}`} placeholder={`Add ${label} and press Enter`}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault();
            const text = e.currentTarget.value.trim();
            if (text) { onChange([...value, text]); e.currentTarget.value = ""; }
          }
        }} />}
    </div>;
  }
  if (value && typeof value === "object") {
    return <div className="space-y-3" role="group" aria-label={label}>
      {Object.entries(value).map(([key, entry]) => <BrandJsonField key={key}
        label={`${label}: ${labelFor(key)}`}
        value={key === "description" && entry == null && typeof (value as Record<string, unknown>).name === "string" ? "" : entry}
        depth={depth + 1}
        onChange={next => onChange({ ...value, [key]: next })} />)}
    </div>;
  }
  return <p className="text-xs text-muted-foreground">
    {label}: {value == null ? "Not provided" : typeof value === "number" || typeof value === "boolean" ? String(value) : "Unsupported value"} (retained unchanged)
  </p>;
}
